// src/components/calendar/EventCreationPanel/EventCreationPanelShadcn.jsx - Updated to Use Hooks
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Calendar, Users, Handshake, Settings, Plus, X } from 'lucide-react';

// 🚀 NEW: Import our shadcn form component library
import {
  SlidePanelShadcn,
  PanelHeaderShadcn,
  PanelContentShadcn
} from '../../ui/panels';

import {
  FormInputShadcn,
  FormTextAreaShadcn,
  FormSelectShadcn,
  FormLabelShadcn,
  FormCardShadcn,
  FormButtonShadcn,
  FormSectionHeaderShadcn,
  ValidationMessageShadcn,
  ConfigToggleFieldShadcn
} from '../../ui/forms';

// 🚀 UPDATED: Use hooks instead of direct services
import { useCustomers } from '../../../hooks/useCustomers';
import { useSettings } from '../../../hooks/useSettings';
import { usePartners } from '../../../hooks/usePartners';
import { useEvents } from '../../../hooks/useEvents';
import { PartnerServiceDetailsCardShadcn } from '../../ui/cards/PartnerServiceDetailsCard';

const EventCreationPanelShadcn = ({
  isOpen,
  onClose,
  isCreatingEvent = false,
  selectedDateTime,
  onDateTimeChange,
  onPreviewUpdate,
  currentCalendarDateRange,  // ← ADD THIS
  onPartnerAvailabilityRequest,
  onClearPartnerAvailability,

}) => {
  const instanceId = useRef(`${Math.random().toString(36).substr(2, 9)}`);
  const renderCount = useRef(0);

  console.log(`🔍 [${instanceId.current}] EventCreationPanel RENDER #${++renderCount.current}`, {
    isOpen,
    selectedDateTime
  });

  // ===============================================
  // 🚀 NEW: HOOKS INSTEAD OF SERVICES
  // ===============================================

  // Customer data hook
  const {
    customers,
    loadCustomers,
    loading: loadingCustomers,
    error: customersError
  } = useCustomers();

  // Services/settings data hook
  const {
    services,
    loadServices,
    loading: loadingServices,
    error: servicesError
  } = useSettings();

  // Partners data hook
  const {
    connectedPartners,
    loadPartnerNetwork,
    partnerServices,
    servicesLoading,
    loadPartnerServices,
    loading: loadingPartners,
    error: partnersError
  } = usePartners();

  const { createEvent } = useEvents();



  // ===============================================
  // FORM STATE MANAGEMENT
  // ===============================================

  // Create initial form state
  const getInitialFormState = useCallback(() => ({
    // Event basics
    eventType: 'general',
    title: '',
    eventDateTime: selectedDateTime,
    duration: 180,
    description: '',

    // Service event fields
    serviceType: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerId: '',
    serviceAddress: '',
    serviceNotes: '',

    // Partner service fields
    partnerId: '',
    partnerServiceId: '',
    partnerName: undefined,
    serviceId: '',
    serviceName: undefined,
    partner: null,
    partnerService: null,

    // Blocking configuration
    isBlocking: true,
    blockingType: 'capacity'
  }), [selectedDateTime]);

  const [formData, setFormData] = useState(getInitialFormState());
  const [error, setError] = useState(null);

  // ===============================================
  // DATA LOADING EFFECTS
  // ===============================================

  // 🚀 UPDATED: Remove .catch() - use emit-only pattern
  useEffect(() => {
    if (isOpen) {
      console.log(`🔍 [${instanceId.current}] Panel opened, loading data`);

      // Load customers for service events - emit-only, no .catch()
      if (customers.length === 0) {
        loadCustomers();
      }

      // Load services for service events - emit-only, no .catch()
      if (services.length === 0) {
        loadServices();
      }

      // Load partners for partner service events - emit-only, no .catch()
      if (connectedPartners.length === 0) {
        loadPartnerNetwork();
      }
    }
  }, [isOpen, customers.length, services.length, connectedPartners.length,]);

  // Load partner services when partner is selected
  useEffect(() => {
    if (formData.partnerId && formData.eventType === 'partner_service') {
      console.log('🔧 Loading services for selected partner:', formData.partnerId);
      loadPartnerServices(formData.partnerId);
    }
  }, [formData.partnerId, formData.eventType, loadPartnerServices]);

  // ===============================================
  // FORM STATE UPDATES
  // ===============================================

  // Update form when selected datetime changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      eventDateTime: selectedDateTime
    }));
  }, [selectedDateTime]);

  // Reset form when panel opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormState());
      setError(null);
    }
  }, [isOpen, getInitialFormState]);

  // ===============================================
  // FORM HANDLERS
  // ===============================================

  const handleInputChange = useCallback((field, value) => {
    console.log(`🔍 [${instanceId.current}] Field change:`, field, '=', value);

    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Clear error when user starts typing
      if (error) {
        setError(null);
      }

      // Trigger preview update with some delay
      if (onPreviewUpdate) {
        setTimeout(() => {
          onPreviewUpdate(newData);
        }, 100);
      }

      return newData;
    });
  }, [error, onPreviewUpdate]);

  // ===============================================
  // PARTNER AVAILABILITY HANDLER
  // ===============================================

  const handlePartnerChange = useCallback((partnerId) => {
    const partner = connectedPartners.find(p => p.id === partnerId);

    // Update form data
    handleInputChange('partnerId', partnerId);

    // If partner selected and we have availability request handler, trigger it
    if (partner && onPartnerAvailabilityRequest && currentCalendarDateRange) {
      console.log('📅 EventCreationPanel: Requesting availability for partner:', partner.business_name);
      onPartnerAvailabilityRequest(partner, currentCalendarDateRange);
    }
  }, [connectedPartners, handleInputChange, onPartnerAvailabilityRequest, currentCalendarDateRange]);

  // Get services for the currently selected partner
  const getPartnerServices = useCallback(() => {
    if (!formData.partnerId || !partnerServices[formData.partnerId]) {
      return [];
    }

    return partnerServices[formData.partnerId].map(service => ({
      value: service.id,
      label: service.name,
      description: service.description,
      duration: service.duration_minutes,
      price: service.price
    }));
  }, [formData.partnerId, partnerServices]);

  // Add service selection handler:
  const handlePartnerServiceChange = useCallback((serviceId) => {
    const selectedService = getPartnerServices().find(s => s.value === serviceId);

    handleInputChange('partnerServiceId', serviceId);

    // Auto-update duration when service is selected
    if (selectedService && selectedService.duration) {
      handleInputChange('duration', selectedService.duration);
    }
  }, [getPartnerServices, handleInputChange]);

  // Close handler
  const handleClose = useCallback(() => {
    console.log(`🔍 [${instanceId.current}] PANEL CLOSING`);

    // Reset form state
    setFormData(getInitialFormState());
    setError(null);

    if (onClose) {
      onClose();
    }
  }, [onClose, getInitialFormState]);

  // ===============================================
  // CUSTOMER MANAGEMENT HANDLERS
  // ===============================================

  const handleCustomerSelect = useCallback((customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      handleInputChange('customerId', customer.id);
      handleInputChange('customerName', customer.name);
      handleInputChange('customerEmail', customer.email);
      handleInputChange('customerPhone', customer.phone);
    }
  }, [customers, handleInputChange]);

  const handleClearCustomerSelection = useCallback(() => {
    handleInputChange('customerId', '');
    // Keep the populated values so user can edit them
  }, [handleInputChange]);

  const handleServiceSelect = useCallback((serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      handleInputChange('serviceType', service.name);
      handleInputChange('serviceId', service.id);
      // Auto-set duration from service
      if (service.duration_minutes) {
        handleInputChange('duration', service.duration_minutes);
      }
    }
  }, [services, handleInputChange]);

  // ===============================================
  // FORM SUBMISSION - UPDATED TO BUTTON CLICK PATTERN
  // ===============================================

  const handleSubmit = useCallback(() => {
    console.log(`🔍 [${instanceId.current}] FORM SUBMITTED:`, formData);

    // Use ISO datetime directly
    const scheduledDateTime = formData.eventDateTime;

    console.log('🌍 Using ISO datetime:', scheduledDateTime);

    // Prepare event data based on type
    let eventData;

    if (formData.eventType === 'partner_service') {
      eventData = {
        type: 'partner_service',
        event_category: 'service',
        title: formData.title,
        scheduled_datetime: scheduledDateTime,
        duration_minutes: formData.duration,
        external_business_id: formData.partnerId,
        requesting_business_name: 'Business Name', // You might want to get this from context
        service_type: formData.partnerServiceId,
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        service_address: formData.serviceAddress
      };
    } else {
      eventData = {
        title: formData.title,
        scheduled_datetime: scheduledDateTime,
        duration_minutes: formData.duration,
        description: formData.description,
        event_category: formData.eventType,

        // Add missing required fields:
        status: 'pending',
        is_blocking: formData.isBlocking,
        blocking_type: formData.blockingType,

        // Service event fields
        ...(formData.eventType === 'service' && {
          // Only send customer_id if we have one (customer was selected from dropdown)
          ...(formData.customerId && { customer_id: formData.customerId }),

          // Always send customer details (for both existing and new customers)
          customer_name: formData.customerName,
          customer_email: formData.customerEmail,
          customer_phone: formData.customerPhone,

          service_address: formData.serviceAddress,
          service_notes: formData.serviceNotes,
          service_type: formData.serviceType,
          job_status: 'scheduled'
        })
      };
    }

    createEvent(eventData);
    handleClose();

  }, [formData, createEvent, handleClose]);

  // ===============================================
  // RENDER EVENT TYPE OPTIONS
  // ===============================================

  const eventTypeOptions = [
    {
      value: 'general',
      label: 'General Event',
      description: 'Block time for meetings, breaks, or other activities'
    },
    {
      value: 'service',
      label: 'Customer Appointment',
      description: 'Schedule a service appointment with a customer'
    },
    {
      value: 'partner_service',
      label: 'Partner Service Booking',
      description: 'Book a service with a partner business'
    }
  ];

  // ===============================================
  // RENDER DURATION OPTIONS
  // ===============================================

  const durationOptions = [
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
    { value: 180, label: '3 hours' },
    { value: 240, label: '4 hours' },
    { value: 480, label: '8 hours (Full day)' }
  ];

  // ===============================================
  // RENDER DATA OPTIONS (using hook data)
  // ===============================================

  // Customer options (from useCustomers hook)
  const customerOptions = useMemo(() => {
    return customers.map(customer => ({
      value: customer.id,
      label: customer.name,
      email: customer.email,
      phone: customer.phone,
      searchText: `${customer.name} ${customer.email} ${customer.phone || ''}`
    }));
  }, [customers]);

  // ✅ ADD THIS - Generate service type options from loaded services
  const serviceTypeOptions = useMemo(() => {
    return services.map(service => ({
      value: service.id,
      label: service.name,
      description: service.description,
      duration: service.duration_minutes,
      price: service.price
    }));
  }, [services]);

  // ✅ ADD THIS - Generate partner options from loaded partners
  const partnerOptions = useMemo(() => {
    return connectedPartners.map(partner => ({
      value: partner.id,
      label: partner.business_name || partner.name
    }));
  }, [connectedPartners]);

  // ===============================================
  // RENDER OPTION FUNCTIONS
  // ===============================================

  const renderCustomerOption = useCallback((option) => (
    <div className="flex items-center gap-3 p-2">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-semibold text-white text-xs">
        {option.label.split(' ').map(n => n[0]).join('').substring(0, 2)}
      </div>
      <div className="flex-1">
        <div className="font-medium text-slate-200">{option.label}</div>
        <div className="text-sm text-slate-400">{option.email}</div>
        {option.phone && <div className="text-sm text-slate-400">{option.phone}</div>}
      </div>
    </div>
  ), []);

  const renderServiceOption = useCallback((option) => (
    <div className="flex items-center justify-between p-2">
      <div className="flex-1">
        <div className="font-medium text-slate-200">{option.label}</div>
        {option.description && (
          <div className="text-sm text-slate-400">{option.description}</div>
        )}
      </div>
      <div className="flex flex-col items-end text-xs text-slate-400">
        {option.duration && (
          <span>{option.duration} min</span>
        )}
        {option.price && (
          <span className="text-emerald-400 font-medium">${option.price}</span>
        )}
      </div>
    </div>
  ), []);

  // ===============================================
  // RENDER LOADING STATES
  // ===============================================

  const isLoadingData = loadingCustomers || loadingServices || loadingPartners;
  const hasDataErrors = customersError || servicesError || partnersError;

  // ===============================================
  // RENDER FORM SECTIONS
  // ===============================================

  return (
    <div className="h-full flex flex-col bg-slate-900/95 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl rounded-r-2xl overflow-hidden">
      <PanelHeaderShadcn
        title={formData.eventType === 'partner_service' ?
          'Book Partner Service' :
          formData.eventType === 'service' ?
          'New Customer Appointment' :
          'New Event'
        }
        subtitle={formData.eventDateTime ?
          new Date(formData.eventDateTime).toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) : ''
        }
        onClose={handleClose}
      />

      <PanelContentShadcn className="flex-1 overflow-y-auto scrollbar-custom">
        <form id="event-form" className="space-y-6">
          {/* Data Loading Indicator */}
          {isLoadingData && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-blue-300 text-sm">Loading form data...</span>
              </div>
            </div>
          )}

          {/* Data Error Indicator */}
          {hasDataErrors && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
              <div className="text-yellow-300 text-sm">
                Some form data may not be available. You can still create basic events.
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <ValidationMessageShadcn type="error" message={error} />
          )}

          {/* Event Type Selection */}
          <FormCardShadcn title="Event Type" icon={Calendar}>
            <FormSelectShadcn
              label="Type of Event"
              value={formData.eventType}
              onChange={(value) => handleInputChange('eventType', value)}
              options={eventTypeOptions}
              required
            />
          </FormCardShadcn>

          {/* Basic Event Details */}
          <FormCardShadcn title="Event Details">
            <div className="space-y-4">
              <FormInputShadcn
                label="Event Title"
                value={formData.title}
                onChange={(value) => handleInputChange('title', value)}
                placeholder="Enter event title..."
                required
              />

              <FormInputShadcn
                label="Event Date & Time"
                type="datetime-local"
                value={formData.eventDateTime ?
                  (() => {
                    const date = new Date(formData.eventDateTime);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    return `${year}-${month}-${day}T${hours}:${minutes}`;
                  })() : ''
                }
                onChange={(value) => {
                  if (value) {
                    // Convert datetime-local to ISO string with timezone
                    const localDateTime = new Date(value);
                    const timezoneOffset = localDateTime.getTimezoneOffset();
                    const hours = Math.floor(Math.abs(timezoneOffset) / 60);
                    const minutes = Math.abs(timezoneOffset) % 60;
                    const sign = timezoneOffset <= 0 ? '+' : '-';
                    const offsetStr = `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                    const isoDateTime = value + ':00' + offsetStr;

                    handleInputChange('eventDateTime', isoDateTime);
                    if (onDateTimeChange) {
                      onDateTimeChange(isoDateTime);
                    }
                  }
                }}
                required
              />

              <FormSelectShadcn
                label="Duration"
                value={formData.duration}
                onChange={(value) => handleInputChange('duration', parseInt(value))}
                options={durationOptions}
                required
              />

              <FormTextAreaShadcn
                label="Description"
                value={formData.description}
                onChange={(value) => handleInputChange('description', value)}
                placeholder="Optional event description..."
                rows={3}
              />
            </div>
          </FormCardShadcn>

          {/* NEW: Blocking Configuration - ONLY for general events */}
          {formData.eventType === 'general' && (
            <FormCardShadcn title="Scheduling Options" icon={Settings}>
              <div className="space-y-4">
                <ConfigToggleFieldShadcn
                  label="Blocks Scheduling"
                  description="Prevents other appointments from being scheduled at this time"
                  value={formData.isBlocking}
                  onChange={(value) => handleInputChange('isBlocking', value)}
                />

                {formData.isBlocking && (
                  <FormSelectShadcn
                    label="Blocking Type"
                    value={formData.blockingType}
                    onChange={(value) => handleInputChange('blockingType', value)}
                    options={[
                      {
                        value: 'capacity',
                        label: 'Capacity Block',
                        description: 'Reduces available appointment slots'
                      },
                      {
                        value: 'full',
                        label: 'Full Block',
                        description: 'Completely blocks the time slot'
                      }
                    ]}
                    renderOption={(option) => (
                      <div>
                        <div className="font-medium text-slate-200">{option.label}</div>
                        <div className="text-sm text-slate-400">{option.description}</div>
                      </div>
                    )}
                  />
                )}
              </div>
            </FormCardShadcn>
          )}

          {/* Service Event Fields */}
          {(formData.eventType === 'service' || formData.eventType === 'partner_service') && (
            <FormCardShadcn title="Customer Information" icon={Users}>
              <div className="space-y-4">
                {formData.eventType === 'service' && (
                  <div className="space-y-4">
                    {serviceTypeOptions.length > 0 ? (
                      <FormSelectShadcn
                        label="Service Type"
                        value={formData.serviceId}
                        onChange={handleServiceSelect}
                        options={serviceTypeOptions}
                        renderOption={renderServiceOption}
                        placeholder="Select service type..."
                        searchable={true}
                      />
                    ) : (
                      <FormInputShadcn
                        label="Service Type"
                        value={formData.serviceType}
                        onChange={(value) => handleInputChange('serviceType', value)}
                        placeholder="Enter service type manually..."
                        required
                      />
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  {/* Customer Selection with Clear Option */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300">Customer</label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <FormSelectShadcn
                          value={formData.customerId}
                          onChange={handleCustomerSelect}
                          options={customerOptions}
                          renderOption={renderCustomerOption}
                          searchable={true}
                          placeholder="Search and select customer or enter details manually..."
                          className="w-full"
                        />
                      </div>
                      {formData.customerId && (
                        <FormButtonShadcn
                          type="button"
                          variant="ghost"
                          onClick={handleClearCustomerSelection}
                          className="px-4 py-3 text-slate-400 hover:text-slate-300"
                          title="Clear selection to enter customer details manually"
                        >
                          <X className="w-4 h-4" />
                        </FormButtonShadcn>
                      )}
                    </div>
                  </div>

                  {/* Customer Details - Always Editable */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInputShadcn
                      label="Customer Name"
                      value={formData.customerName}
                      onChange={(value) => handleInputChange('customerName', value)}
                      placeholder="Customer name..."
                      required
                    />
                    <FormInputShadcn
                      label="Customer Email"
                      type="email"
                      value={formData.customerEmail}
                      onChange={(value) => handleInputChange('customerEmail', value)}
                      placeholder="customer@email.com"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInputShadcn
                      label="Customer Phone"
                      value={formData.customerPhone}
                      onChange={(value) => handleInputChange('customerPhone', value)}
                      placeholder="(555) 123-4567"
                    />
                    <FormTextAreaShadcn
                      label="Service Address"
                      value={formData.serviceAddress}
                      onChange={(value) => handleInputChange('serviceAddress', value)}
                      placeholder="Full address where service will be performed..."
                      rows={3}
                      required
                    />
                  </div>

                  <div className="text-xs text-slate-400 bg-slate-800/30 rounded-lg p-3 border border-slate-700/30">
                    💡 <strong>Tip:</strong> Select an existing customer from the dropdown above, or fill in the details manually to create a new customer automatically.
                  </div>
                </div>

                <FormTextAreaShadcn
                  label="Service Notes"
                  value={formData.serviceNotes}
                  onChange={(value) => handleInputChange('serviceNotes', value)}
                  placeholder="Additional notes about the service..."
                  rows={3}
                />
              </div>
            </FormCardShadcn>
          )}

          {/* Partner Service Fields */}
          {formData.eventType === 'partner_service' && (
            <FormCardShadcn title="Partner Information" icon={Handshake}>
              <div className="space-y-6">
                {/* Step 1: Select Partner */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                      <span className="text-blue-400 text-sm font-semibold">1</span>
                    </div>
                    <h4 className="text-sm font-semibold text-slate-300">Select Partner</h4>
                  </div>

                  <FormSelectShadcn
                    label="Partner Business"
                    value={formData.partnerId}
                    onChange={handlePartnerChange}
                    options={partnerOptions}
                    placeholder="Select partner business..."
                    disabled={loadingPartners}
                  />
                </div>

                {/* Step 2: Select Service */}
                {formData.partnerId && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                        <span className="text-purple-400 text-sm font-semibold">2</span>
                      </div>
                      <h4 className="text-sm font-semibold text-slate-300">Select Service</h4>
                    </div>

                    <FormSelectShadcn
                      label="Partner Service"
                      value={formData.partnerServiceId}
                      onChange={handlePartnerServiceChange}
                      options={getPartnerServices()}
                      placeholder={servicesLoading ? "Loading services..." : "Select service..."}
                      disabled={servicesLoading || getPartnerServices().length === 0}
                      required
                    />

                    {/* Show service details when selected */}
                    {formData.partnerServiceId && (() => {
                      const selectedService = getPartnerServices().find(s => s.value === formData.partnerServiceId);
                      return selectedService && (
                        <div className="pl-8">
                          <PartnerServiceDetailsCardShadcn
                            service={selectedService}
                          />
                        </div>
                      );
                    })()}

                    {formData.partnerId && !servicesLoading && getPartnerServices().length === 0 && (
                      <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3 ml-8">
                        This partner has no available services for booking.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </FormCardShadcn>
          )}
        </form>
      </PanelContentShadcn>

      {/* Sticky Footer with Form Actions */}
      <div className="border-t border-slate-700/50 p-6 bg-slate-900/50 backdrop-blur-sm">
        <div className="flex gap-3">
          <FormButtonShadcn
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isCreatingEvent}
            className="flex-1"
          >
            Cancel
          </FormButtonShadcn>

          <FormButtonShadcn
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={isCreatingEvent || !formData.title}
            loading={isCreatingEvent}
            className="flex-1"
          >
            {isCreatingEvent ? 'Creating...' : 'Create Event'}
          </FormButtonShadcn>
        </div>
      </div>
    </div>
  );
};

export default EventCreationPanelShadcn;